import { CurrencyCode } from "@outschool/gql-backend-generated";
import { useLookupIP } from "@outschool/iplookup-client";
import { useLocalStorageState } from "@outschool/local-storage";
import {
  COUNTRIES_INFO,
  CountryCode,
  CurrencyLocalizationContext,
  I18nLocale,
  LocaleProvider,
  SELECTED_CURRENCY_CODE_LOCAL_STORAGE_KEY,
  SetCurrencyLocalizationContext,
  currencyCodeFromString,
  getCountryFromCurrency,
  getCurrencyCodeFromLocaleAndIP,
  useLocale,
} from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import React from "react";

import { useUpdateCurrentUserMutation } from "../queries/CurrentUser";
import { PseudoLocalizationProvider } from "./PseudoLocalizationProvider";
import { TimeZoneProvider } from "./TimeZoneProvider";

const CurrencyProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const locale = useLocale();
  const { ipInfo, ipInfoLoaded } = useLookupIP();
  const {
    currentUser,
    isLoggedIn,
    currentUserIsLoading,
    currentUserHasLoaded,
  } = useSession();

  const currentUserDefaultCurrencyCode = currentUser?.default_currency
    ? currencyCodeFromString(currentUser.default_currency)
    : null;
  const [updateCurrentUser] = useUpdateCurrentUserMutation();

  const [selectedCurrencyLocalStorage, setSelectedCurrencyLocalStorage] =
    useLocalStorageState<CurrencyCode | null>(
      SELECTED_CURRENCY_CODE_LOCAL_STORAGE_KEY,
      null,
    );

  React.useEffect(() => {
    // This hook should only run when a user does not have a saved currency in local storage
    if (selectedCurrencyLocalStorage) {
      return;
    }
    // If currentUser is still loading, wait until it finishes so we can check currentUserDefaultCurrencyCode
    // If ipInfo is still loading, wait until it finishes so we can map from IP to currency
    if (currentUserIsLoading || !ipInfoLoaded) {
      return;
    }
    // If user is logged in and has a currency code associated with their account,
    // update local storage to match currentUserDefaultCurrencyCode
    if (isLoggedIn && currentUserDefaultCurrencyCode) {
      setSelectedCurrencyLocalStorage(currentUserDefaultCurrencyCode);
    } else {
      // For logged-out users, use a combination of user IP and locale
      // to save a default currency into local storage
      setSelectedCurrencyLocalStorage(
        getCurrencyCodeFromLocaleAndIP(locale, ipInfo),
      );
    }
  }, [
    selectedCurrencyLocalStorage,
    setSelectedCurrencyLocalStorage,
    currentUserIsLoading,
    isLoggedIn,
    currentUserDefaultCurrencyCode,
    locale,
    ipInfoLoaded,
    ipInfo,
  ]);

  const setCurrency = React.useCallback(
    async (newCurrencyCode: CurrencyCode) => {
      try {
        if (isLoggedIn) {
          await updateCurrentUser({
            variables: {
              default_currency: newCurrencyCode,
            },
          });
        }
        setSelectedCurrencyLocalStorage(newCurrencyCode);
      } catch (err) {
        console.error(err);
      }
    },
    [isLoggedIn, updateCurrentUser, setSelectedCurrencyLocalStorage],
  );

  // Map to get country info associated with the currency set in current_user, local storage,
  // or default to US
  const countryCode: CountryCode =
    getCountryFromCurrency(currentUserDefaultCurrencyCode) ||
    getCountryFromCurrency(selectedCurrencyLocalStorage) ||
    CountryCode.US;
  const selectedCountryInfo = COUNTRIES_INFO[countryCode];

  // Use currency code from current_user if user is logged in and it exists,
  // otherwise use the currency saved in local storage or default to US
  const currencyCode =
    currentUserDefaultCurrencyCode ||
    selectedCurrencyLocalStorage ||
    CurrencyCode.Usd;

  const currentUserExists = !!currentUser;
  React.useEffect(() => {
    // Update user's default_currency in the event they don't have one, but only
    // if they're *fully* logged in.
    if (currentUserExists && !currentUserDefaultCurrencyCode && currencyCode) {
      updateCurrentUser({
        variables: {
          default_currency: currencyCode,
        },
      });
    }
  }, [
    currentUserExists,
    currentUserDefaultCurrencyCode,
    currencyCode,
    updateCurrentUser,
  ]);

  return (
    <SetCurrencyLocalizationContext.Provider value={{ setCurrency }}>
      <CurrencyLocalizationContext.Provider
        value={{
          currencyCode,
          countryCode,
          selectedCurrencyCountry: selectedCountryInfo,
          isLoading: currentUserIsLoading && !ipInfoLoaded,
          hasLoaded: currentUserHasLoaded,
        }}
      >
        {children}
      </CurrencyLocalizationContext.Provider>
    </SetCurrencyLocalizationContext.Provider>
  );
};

export const LocalizationProvider = React.memo(
  ({
    children,
    userSelectedLocale,
  }: React.PropsWithChildren<{ userSelectedLocale?: I18nLocale }>) => (
    <LocaleProvider userSelectedLocale={userSelectedLocale}>
      <PseudoLocalizationProvider>
        <TimeZoneProvider>
          <CurrencyProvider>{children}</CurrencyProvider>
        </TimeZoneProvider>
      </PseudoLocalizationProvider>
    </LocaleProvider>
  ),
);

interface WithLocaleProps {
  locale: I18nLocale;
}

export function withLocale<T>() {
  return (WrappedComponent: React.ComponentType<T & WithLocaleProps>) =>
    (props: T) => {
      const locale = useLocale();
      return <WrappedComponent {...props} locale={locale} />;
    };
}
